import * as React from 'react'

import { Box, Divider, Grid, Spinner, Text } from '@factorialco/deprecated-design-system'
import { PerformanceQuestionSingleLabel } from 'generated/resources/datatable.graphql'
import {
  PerformanceProcessPersonalResultsAgreementDocument,
  PerformanceProcessPersonalResultsDocument,
  PerformanceProcessPersonalResultsEvaluationsDocument,
  PerformanceProcessPersonalResultsTargetWithEvaluationsFragment,
  PerformanceQuestionnaireByReviewerStrategyFragment,
  PerformanceReviewerStrategiesStrategyEnum,
} from 'generated/resources/graphql'
import { compact, groupBy, mapValues, sortBy } from 'lodash'

import { useCurrentSession } from 'modules/core/hooks/useCurrentSession'
import { useQuery } from 'modules/core/hooks/useQuery'
import i18n from 'modules/core/lib/i18n'

import { CompetenciesChart } from 'modules/performance_process/components/CompetenciesChart'
import { TargetAnswersTable } from 'modules/performance_process/components/TargetAnswersTable'
import { ActionItemList } from 'modules/performance_process/components/agreements/ActionItemList'
import { AgreementCourse } from 'modules/performance_process/components/agreements/AgreementCourse'
import { TargetFinalScoreCard } from 'modules/performance_process/components/common/TargetFinalScoreCard'
import FullAnswerSidepanel from 'modules/performance_process/components/insights/Answers/AllAnswersTab/FullAnswerSidepanel'
import { TargetInsights } from 'modules/performance_process/components/insights/TargetInsight'
import { Answer } from 'modules/performance_process/components/insights/common/presenters'
import * as styles from 'modules/performance_process/components/review/show/PersonalResults/index.vanilla.css'
import { TargetAgreementCard } from 'modules/performance_process/components/review/show/TargetAgreementCard'
import { EvaluationReviewerCard } from 'modules/performance_process/components/targetEvaluations/EvaluationReviewerCard'
import { useInitiateAgreement } from 'modules/performance_process/lib/agreementDialog'
import { useNewPerformanceModuleContext } from 'modules/performance_process/pages/ReviewsRootPage'

interface TargetAnswersSectionProps {
  reviewType: PerformanceReviewerStrategiesStrategyEnum
  title: string
  target: PerformanceProcessPersonalResultsTargetWithEvaluationsFragment
  reviewQuestionnaireByReviewerStrategy: PerformanceQuestionnaireByReviewerStrategyFragment
  onSelectQuestion: (question: PerformanceQuestionSingleLabel) => void
  onSelectAnswer: (answer: Answer) => void
}

const TargetAnswersSection: React.FC<TargetAnswersSectionProps> = ({
  reviewType,
  title,
  target,
  reviewQuestionnaireByReviewerStrategy,
  onSelectQuestion,
  onSelectAnswer,
}) => {
  const publishedEvaluations = target.evaluations.filter(
    (evaluation) => evaluation.status === 'published'
  )

  return (
    <Box gap="s0">
      <Text.Normal>{title}</Text.Normal>
      <TargetAnswersTable
        reviewType={reviewType}
        target={{ ...target, evaluations: publishedEvaluations }}
        reviewQuestionnaireByReviewerStrategy={reviewQuestionnaireByReviewerStrategy}
        onSelectQuestion={onSelectQuestion}
        onSelectAnswer={onSelectAnswer}
      />
    </Box>
  )
}

export const PersonalResults: React.FC<{
  reviewProcessTargetId: string
  isCheckingOwnResults?: boolean
}> = ({ reviewProcessTargetId, isCheckingOwnResults = false }) => {
  const { access } = useCurrentSession()
  const { hasCustomScoreConfigurationEnabled } = useNewPerformanceModuleContext()

  const [selectedQuestion, setSelectedQuestion] =
    React.useState<PerformanceQuestionSingleLabel | null>(null)
  const [selectedAnswer, setSelectedAnswer] = React.useState<Answer | null>(null)

  const { data, loading } = useQuery(PerformanceProcessPersonalResultsDocument, {
    variables: { reviewProcessTargetId },
  })

  const { data: evaluationsData, loading: evaluationsLoading } = useQuery(
    PerformanceProcessPersonalResultsEvaluationsDocument,
    {
      variables: { reviewProcessTargetId },
    }
  )

  const { data: agreementData, loading: agreementLoading } = useQuery(
    PerformanceProcessPersonalResultsAgreementDocument,
    {
      variables: { reviewProcessTargetId },
    }
  )

  const target = data?.performance?.reviewProcessTarget
  const review = target?.performanceReviewProcess
  const targetWithEvaluations = evaluationsData?.performance?.reviewProcessTarget
  const targetWithAgreement = agreementData?.performance?.reviewProcessTarget
  const agreement = targetWithAgreement?.agreement

  const { initiateAgreement } = useInitiateAgreement({
    republishEvaluationsEnabled: review?.republishEvaluationsConfiguration?.enabled ?? false,
  })

  if (loading || evaluationsLoading || agreementLoading) {
    return (
      <Box flexGrow alignItems="center" justifyContent="center">
        <Spinner />
      </Box>
    )
  }

  if (!target || !review || !targetWithEvaluations || !targetWithAgreement) return null

  const reviewQuestionnaireByReviewerStrategy = review?.questionnaireByReviewerStrategies
  const evaluations = targetWithEvaluations?.evaluations ?? []

  const scoreConfiguration = review?.scoreConfiguration
  const canSeeFinalScore = Boolean(target._policies?.seeTargetFinalScore?.can)

  const managerAnsweredCompetencies =
    evaluations.find((evaluation) => evaluation?.evaluationType === 'manager')
      ?.answeredQuestionnaires?.answeredCompetenciesQuestionnaire?.content ?? []
  const selfAnsweredCompetencies =
    evaluations.find((evaluation) => evaluation?.evaluationType === 'self')?.answeredQuestionnaires
      ?.answeredCompetenciesQuestionnaire?.content ?? []

  const sortedEvaluations = sortBy(evaluations, (evaluation) =>
    evaluation?.reviewerAccess?.current ? -1 : 0
  )
  const publishedEvaluations = evaluations.filter((evaluation) => evaluation.status === 'published')
  const reviewerIdsByStrategy = mapValues(groupBy(evaluations, 'evaluationType'), (evals) =>
    compact(evals.map((e) => e.reviewerAccess?.id))
  )

  const showFinalScore = scoreConfiguration && canSeeFinalScore
  const showInsights = access.id !== targetWithAgreement.access?.id
  const showAgreementTask = agreement || targetWithAgreement._policies?.initiate?.can
  const showAgreementActionItems = agreement && agreement.actionItemsInfo.length > 0
  const showCompetenciesChart =
    !!selfAnsweredCompetencies.length && !!managerAnsweredCompetencies.length
  const showAnswersTables = reviewQuestionnaireByReviewerStrategy && publishedEvaluations.length > 0

  const hasPublishedEvaluations = (evaluationType: PerformanceReviewerStrategiesStrategyEnum) =>
    publishedEvaluations.some((evaluation) => evaluation.evaluationType === evaluationType)

  return (
    <Box width="full" gap="s32">
      {(showFinalScore || showInsights) && (
        <Box gap="s16">
          {showFinalScore && (
            <Grid variant="fixed">
              <TargetFinalScoreCard
                scoreConfiguration={scoreConfiguration}
                targetWithScores={target}
              />
            </Grid>
          )}
          {showFinalScore && showInsights && <Divider />}
          {showInsights && (
            <TargetInsights
              reviewId={review.id}
              targetId={Number(targetWithAgreement.access?.id)}
            />
          )}
        </Box>
      )}

      {showAgreementTask && (
        <TargetAgreementCard
          target={targetWithAgreement}
          reviewProcessId={review.id}
          isCheckingOwnAgreement={isCheckingOwnResults}
          onInitiate={() =>
            initiateAgreement({
              targetId: reviewProcessTargetId,
              processId: review.id,
              targetName: targetWithAgreement.access?.fullName ?? '',
            })
          }
        />
      )}

      {showAgreementActionItems && (
        <Box gap="s16">
          <Text.Normal weight="semibold" size="300">
            {i18n.t('performance_process.targets.agreed_action_items')}
          </Text.Normal>
          <ActionItemList agreement={agreement} />
        </Box>
      )}

      {showCompetenciesChart && (
        <Box gap="s16">
          <Text weight="semibold" size="300">
            {i18n.t('performance_process.overview.competency_assessment_title')}
          </Text>
          <CompetenciesChart
            scoreScale={review?.scoreScale?.scale ?? []}
            managerContent={managerAnsweredCompetencies}
            selfContent={selfAnsweredCompetencies}
          />
        </Box>
      )}

      {agreement && <AgreementCourse agreement={agreement} />}

      <Box gap="s16">
        <Text weight="semibold" size="300">
          {i18n.t('performance_process.overview.tasks.reviews.title')}
        </Text>
        <Box flexDirection="row" flexWrap="wrap" gap="s16">
          {sortedEvaluations.map((evaluation) => (
            <Box className={styles.card} key={evaluation.id}>
              <EvaluationReviewerCard
                key={evaluation.id}
                evaluation={evaluation}
                targetId={reviewProcessTargetId}
                reviewId={Number(review?.id)}
                isCheckingOwnResults={isCheckingOwnResults}
                strategyReviewerAccessIds={reviewerIdsByStrategy[evaluation.evaluationType] ?? []}
                showEvaluationScore={hasCustomScoreConfigurationEnabled}
              />
            </Box>
          ))}
        </Box>
      </Box>

      {showAnswersTables && (
        <Box gap="s16">
          <Text.Normal weight="semibold" size="300">
            {i18n.t('performance_process.overview.results.answers_tab')}
          </Text.Normal>
          <Box gap="s32">
            {hasPublishedEvaluations('manager') && (
              <TargetAnswersSection
                reviewType="manager"
                title={i18n.t(
                  'performance_process.reviews.answer_evaluation.evaluation_type.manager.title'
                )}
                target={targetWithEvaluations}
                reviewQuestionnaireByReviewerStrategy={reviewQuestionnaireByReviewerStrategy}
                onSelectQuestion={setSelectedQuestion}
                onSelectAnswer={setSelectedAnswer}
              />
            )}
            {hasPublishedEvaluations('self') && (
              <TargetAnswersSection
                reviewType="self"
                title={i18n.t(
                  'performance_process.reviews.answer_evaluation.evaluation_type.self.title'
                )}
                target={targetWithEvaluations}
                reviewQuestionnaireByReviewerStrategy={reviewQuestionnaireByReviewerStrategy}
                onSelectQuestion={setSelectedQuestion}
                onSelectAnswer={setSelectedAnswer}
              />
            )}
            {hasPublishedEvaluations('direct_reports') && (
              <TargetAnswersSection
                reviewType="direct_reports"
                title={i18n.t(
                  'performance_process.reviews.answer_evaluation.evaluation_type.direct_reports.title'
                )}
                target={targetWithEvaluations}
                reviewQuestionnaireByReviewerStrategy={reviewQuestionnaireByReviewerStrategy}
                onSelectQuestion={setSelectedQuestion}
                onSelectAnswer={setSelectedAnswer}
              />
            )}
            {hasPublishedEvaluations('peers') && (
              <TargetAnswersSection
                reviewType="peers"
                title={i18n.t(
                  'performance_process.reviews.answer_evaluation.evaluation_type.peers.title'
                )}
                target={targetWithEvaluations}
                reviewQuestionnaireByReviewerStrategy={reviewQuestionnaireByReviewerStrategy}
                onSelectQuestion={setSelectedQuestion}
                onSelectAnswer={setSelectedAnswer}
              />
            )}

            {selectedAnswer && selectedQuestion && (
              <FullAnswerSidepanel
                question={selectedQuestion}
                answer={selectedAnswer}
                onClose={() => setSelectedAnswer(null)}
              />
            )}
          </Box>
        </Box>
      )}
    </Box>
  )
}
